import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

const issuer = import.meta.env.VITE_OIDC_ISSUER || "https://id.ruishitong.cn/idp/realms/hololive";
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID || "hl-app";
const registrationHintKey = "hl-app.registration.id-token";
const redirectUri = `${window.location.origin}/auth/callback`;
let renewInFlight: Promise<User | null> | null = null;

export const oidc = new UserManager({
  authority: issuer,
  client_id: clientId,
  redirect_uri: redirectUri,
  post_logout_redirect_uri: window.location.origin,
  response_type: "code",
  scope: "openid profile email",
  loadUserInfo: false,
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
});

export function registrationLogoutUrl(idToken?: string): string {
  const logout = new URL(`${issuer}/protocol/openid-connect/logout`);
  logout.searchParams.set("client_id", clientId);
  logout.searchParams.set("post_logout_redirect_uri", `${window.location.origin}/?hl_register=1`);
  if (idToken) logout.searchParams.set("id_token_hint", idToken);
  return logout.toString();
}

export async function prepareRegistration(): Promise<string> {
  const user = await oidc.getUser();
  const idToken = user?.id_token || window.sessionStorage.getItem(registrationHintKey) || undefined;
  await oidc.removeUser();
  window.sessionStorage.removeItem(registrationHintKey);
  return registrationLogoutUrl(idToken);
}

export function rememberRegistrationHint(user: User | null): void {
  if (user?.id_token) window.sessionStorage.setItem(registrationHintKey, user.id_token);
}

export interface AuthSnapshot {
  user: User | null;
  loading: boolean;
  error?: string;
}

export async function finishCallback(): Promise<User | null> {
  if (!window.location.pathname.endsWith("/auth/callback")) return null;
  const user = await oidc.signinCallback();
  window.history.replaceState({}, document.title, "/");
  return user ?? null;
}

export async function getCurrentUser(): Promise<User | null> {
  const user = await oidc.getUser();
  if (!user) return null;
  if (!user.expired) return user;
  return renewUser();
}

export async function renewUser(): Promise<User | null> {
  if (renewInFlight) return renewInFlight;
  renewInFlight = (async () => {
    try {
      return await oidc.signinSilent();
    } catch {
      await oidc.removeUser();
      return null;
    } finally {
      renewInFlight = null;
    }
  })();
  return renewInFlight;
}

export function profileLabel(user: User | null): string {
  if (!user) return "";
  const profile = user.profile as Record<string, unknown>;
  return String(profile.email || profile.name || profile.preferred_username || profile.sub || "Signed in user");
}
